import React, { useMemo } from "react";
import { ITournament } from "@/redux/tournaments/types";
import { TournamentsItem } from "../TournamentsItem/index";
import { filterPastTournaments } from "@/utils/tournaments/tournamentHelpers";
import { groupByDate } from "@/utils/date/dateGrouping";
import { DateSection } from "@/components/DateSection";
import { useAppSelector } from "@/redux/store";
import {
  selectMyApplicationsTournaments,
  selectMyCreatedTournaments,
  selectTournamentsLoading,
} from "@/redux/tournaments";
import styles from "./AvailableTournamentsScreen.module.scss";
import { EmptyGameList } from "@/pages/Games/components/EmptyGameList/EmptyGameList";
import { TournamentSkeleton } from "../TournamentSkeleton";

interface AvailableTournamentsScreenProps {
  listTournaments: ITournament[];
  setIsRequiredAuthOpened: (value: boolean) => void;
}

export const AvailableTournamentsScreen: React.FC<AvailableTournamentsScreenProps> = ({
  listTournaments,
  setIsRequiredAuthOpened,
}) => {
  const myApplicationsTournaments = useAppSelector(selectMyApplicationsTournaments);
  const myCreatedTournaments = useAppSelector(selectMyCreatedTournaments);
  const isLoading = useAppSelector(selectTournamentsLoading);

  const activeTournaments = useMemo(() => {
    const filtered = filterPastTournaments(listTournaments);

    return filtered.map((tournament) => {
      const isApplied = myApplicationsTournaments.some(
        (applied) => applied.id === tournament.id
      );
      const isCreated = myCreatedTournaments.some(
        (created) => created.id === tournament.id
      );

      return {
        ...tournament,
        is_enjoying: isApplied,
        is_created: isCreated,
      };
    });
  }, [listTournaments, myApplicationsTournaments, myCreatedTournaments]);

  const dateGroups = useMemo(() => groupByDate(activeTournaments), [activeTournaments]);

  if (isLoading) {
    return (
      <div className={styles.skeletonList}>
        {[...Array(6)].map((_, index) => (
          <TournamentSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (activeTournaments.length === 0) {
    return (
      <EmptyGameList
        title="Ой, список турниров пуст!"
        description="Мы скоро добавим новые турниры. Следите за обновлениями!"
        showButton={false}
      />
    );
  }

  return (
    <div className={styles.list}>
      {dateGroups.map((group) => (
        <DateSection key={group.dateLabel} dateLabel={group.dateLabel}>
          {group.items.map((tournament) => {
            const isAppliedTournament = tournament.is_enjoying === true;
            const isCreateTournaments = tournament.is_created === true;

            return (
              <TournamentsItem
                key={tournament.id}
                item={tournament}
                isAppliedTournament={isAppliedTournament}
                isCreateTournaments={isCreateTournaments}
                setIsRequiredAuthOpened={setIsRequiredAuthOpened}
              />
            );
          })}
        </DateSection>
      ))}
    </div>
  );
};
